import { useTranslation } from 'react-i18next';
import { VERANTWOORDING_ANKER, useMeldVerantwoording } from './VerantwoordingAnker';
import { effectieveDrempel } from '../drempel';
import { formatDatum, formatGetal, isGetal } from '../format';
import type { Verantwoording } from '../work-quality.api';

export interface AccountabilityFooterProps {
  /**
   * Wat er onder de cijfers hoort te staan — niet meer dan dat.
   *
   * `Verantwoording` en niet `Afbakening`: het overzicht van alle medewerkers draagt geen
   * `uitgeslotenVelden` (`MedewerkerslijstAfbakening` in `presentatie.ts`), en de voet moet daar
   * kunnen zeggen dat dit antwoord ze niet noemt in plaats van te beweren dat er geen zijn.
   */
  afbakening: Verantwoording;
  /**
   * De voet van het weekrapport per werksoort (SPEC-mijn-feedback-weekrapport, meetversie 10).
   *
   * Daar geldt geen drempel per persoon maar een drempel per veld, en een aanpassing heeft een
   * uitleg nodig. De regel over de drempel per persoon zou hier iets beweren dat niet meer klopt.
   */
  weekrapport?: boolean;
  /**
   * Het weekscherm van de medewerker (besluit 19-10 #2, 28-10 #3): de grens is 20 per werksoort per week, nooit
   * lager, of de hogere drempel per persoon die de dienst stuurt (M3) — dezelfde grens als het Onder-de-grens-blok.
   * Het oude persoonsdetail houdt zijn eigen regel over de drempel per veld.
   */
  weekscherm?: boolean;
  /** Het niveau van de visueel verborgen kop; 3 binnen het Zo-ziet-kader (story 9, 11). */
  kopNiveau?: 2 | 3 | 4;
}

/**
 * Stap 5 van de leesvolgorde: de verantwoordingsvoet (meetregel 7, UX-DR3).
 *
 * Vier regels, altijd zichtbaar, uitgeschreven. Geen accordeon, geen tooltip, geen info-icoon: de
 * afbakening is geen extraatje bij de cijfers maar de voorwaarde waaronder ze iets betekenen, en
 * wat je moet openklappen lees je niet.
 *
 * **De voet staat op het vlak van de kaarten erboven en niet op de paginakleur.** Gemeten op
 * 4 september 2026 in een echte browser: `--foreground-muted` op `--app-bg` haalt bij 12px een
 * contrast van 4,20:1, en WCAG 2.2 AA vraagt er 4,5. Op `--background` is dat 4,76 en klopt het.
 * Alle drie de tokens die `DESIGN.md` voor deze voet voorschrijft — de gedempte tekst, de 12px en
 * de bovenrand — blijven daarmee staan; wat verandert is het vlak, en dat schreef zij niet voor.
 */
export function AccountabilityFooter({ afbakening, weekrapport = false, weekscherm = false, kopNiveau = 2 }: AccountabilityFooterProps) {
  const { t, i18n } = useTranslation();
  const Kop = `h${kopNiveau}` as const;
  const locale = i18n.resolvedLanguage ?? i18n.language;
  // Zolang deze voet er staat, mag de schil de skip-link ernaartoe tekenen.
  useMeldVerantwoording();
  // Drie gevallen en geen twee: een lijst met namen, een lege lijst (deze week is er niets
  // uitgesloten) en geen lijst (dit antwoord draagt hem niet). Die laatste twee op één hoop gooien
  // maakt van "wij weten het hier niet" de bewering "er zijn er geen", en dat is niet hetzelfde.
  // Alleen tekst: `Intl.ListFormat` werpt op een object of `null` in de lijst (story 12, "werpt nooit").
  const velden = Array.isArray(afbakening.uitgeslotenVelden)
    ? afbakening.uitgeslotenVelden.filter((veld): veld is string => typeof veld === 'string')
    : null;

  return (
    <section data-werkkwaliteit-sectie="verantwoording">
      <div
        className="border-t border-[var(--border-soft)] bg-[var(--background)] px-5 py-3.5 text-[12px] text-[var(--foreground-muted)]"
        data-testid="werkkwaliteit-verantwoording"
        id={VERANTWOORDING_ANKER}
        // Zonder dit springt de pagina wel, maar blijft de focus bij de link achter — en dan loopt
        // wie met het toetsenbord werkt de tabellen alsnog door.
        tabIndex={-1}
      >
        <Kop className="sr-only">{t('qa.eigen.verantwoordingKop')}</Kop>
        <ul className="flex flex-col gap-1">
          <li>
            {t('qa.eigen.verantwoordingPeriode', {
              week: afbakening.weekKey,
              van: formatDatum(afbakening.since, locale),
              tot: formatDatum(afbakening.until, locale),
            })}
          </li>
          {weekrapport ? (
            <>
              <li>
                {weekscherm
                  ? t('qa.eigen.weekrapportVerantwoordingDrempelPerWerksoort', {
                      drempel: formatGetal(effectieveDrempel(afbakening.drempelProductenPerPersoon), locale),
                    })
                  : t('qa.eigen.weekrapportVerantwoordingDrempel', {
                      drempel: isGetal(afbakening.drempelProductenPerVeld) ? formatGetal(afbakening.drempelProductenPerVeld, locale) : t('qa.eigen.nietGemeten'),
                    })}
              </li>
              <li>{t('qa.eigen.weekrapportVerantwoordingAanpassing')}</li>
            </>
          ) : (
            <li>
              {t('qa.eigen.verantwoordingDrempel', {
                drempel: isGetal(afbakening.drempelProductenPerPersoon)
                  ? formatGetal(afbakening.drempelProductenPerPersoon, locale)
                  : t('qa.eigen.nietGemeten'),
              })}
            </li>
          )}
          {/* De uitgesloten velden staan bij naam en zonder telling: ze worden niet gemeten, dus er
              is geen aantal te tonen zonder ze alsnog te tellen (meetregel 1, FR8). De opsomming
              gaat door `Intl.ListFormat`, zodat het scheidingsteken bij de taal hoort en niet in
              de code staat. Een pad breekt overal af: anders duwt een lang veldpad de pagina op
              375px opzij (gemeten 1 november 2026, story 12, donker en "Extra groot"). */}
          <li className="[overflow-wrap:anywhere]">
            {velden === null
              ? t('qa.eigen.verantwoordingUitgeslotenOnbekend')
              : velden.length === 0
                ? t('qa.eigen.verantwoordingUitgeslotenGeen')
                : t('qa.eigen.verantwoordingUitgeslotenVelden', {
                    velden: new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format([...velden]),
                  })}
          </li>
          <li>{t('qa.eigen.verantwoordingZelfAfgetekend')}</li>
        </ul>
      </div>
    </section>
  );
}
